import { Theme, ToggleButton, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ResyncIcon from 'components/icons/Resync/ResyncIcon';
import { getStyleVariables } from 'styles/vars';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSelectedTool,
  getFocusedDifference,
  app,
  getShowDifferenceBoxes,
  getShowTextHighlights,
  getHideOtherDifferences,
} from 'store';
import { PDFTronTools } from 'types';
import { RESYNC_ALLOWED_TYPES } from 'pdftron/docManager/AnnotationTools/ResyncZone';
import selectTool from 'utils/selectTool/selectTool';
import { useEffect, useMemo, useRef } from 'react';
import { PDFManagerFactory } from 'pdftron';
import store from 'store/store';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVars = getStyleVariables(theme);
  return {
    toggleButton: {
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      padding: 0,
      margin: theme.spacing(0.25),
      color: 'rgba(255,255,255,.7)',
      border: 'none',
      '&:hover': {
        color: 'rgba(255,255,255)',
        backgroundColor: 'rgba(255,255,255,.04)',
      },
      '&.Mui-selected': {
        backgroundColor: styleVars.colors.red,
      },
      '&.Mui-selected:hover': {
        backgroundColor: styleVars.colors.red,
      },
      '&.Mui-disabled': {
        color: 'rgba(255,255,255,.38) !important',
      },
    },
    icon: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      cursor: 'pointer',
      '.Mui-disabled &': {
        cursor: 'default',
      },
    },
    disabled: {
      color: 'rgba(255,255,255,.38) !important',
    },
  };
});

interface ResyncToggleProps {
  disabled?: boolean;
}

const ResyncToggle = ({ disabled = false }: ResyncToggleProps) => {
  const { classes } = useStyles();
  const selectedTool = useSelector(getSelectedTool);
  const focusedDifference = useSelector(getFocusedDifference);
  const dispatch = useDispatch();
  const savedToggleValuesRef = useRef<{ boxes: boolean; highlights: boolean; hideOthers: boolean } | null>(null);

  useEffect(() => {
    if (selectedTool !== PDFTronTools.RESYNC) {
      const pdfManager = PDFManagerFactory.getPDFDocManager();
      if (pdfManager) {
        pdfManager.deleteResyncZones();
        pdfManager.deleteResyncCrossouts();
      }
    }
  }, [selectedTool]);

  const updateToggleValues = (makeVisible: boolean) => {
    const boxes = getShowDifferenceBoxes(store.getState());
    const highlights = getShowTextHighlights(store.getState());
    const hideOthers = getHideOtherDifferences(store.getState());

    // if it's null it means you are toggling sync on, you already know you want them set to true no matter what
    if (makeVisible) {
      if (!boxes) {
        dispatch(app.actions.setShowDifferenceBoxes(true));
      }
      // Hide other differences hides unselected boxes outright, so forcing boxes on is not enough:
      // turn it off too, otherwise those boxes cannot be seen or clicked while re-inspecting.
      if (hideOthers) {
        dispatch(app.actions.setHideOtherDifferences(false));
      }
      if (!highlights) {
        // delay to prevent race conditions that cause text highlights not to display
        setTimeout(() => {
          dispatch(app.actions.setShowTextHighlights(true));
        }, 10);
      }
      savedToggleValuesRef.current = { boxes, highlights, hideOthers };
    } else {
      // if you have previous means you are turning it off
      dispatch(app.actions.setShowDifferenceBoxes(savedToggleValuesRef.current?.boxes ?? boxes));
      dispatch(app.actions.setHideOtherDifferences(savedToggleValuesRef.current?.hideOthers ?? hideOthers));
      setTimeout(() => {
        dispatch(app.actions.setShowTextHighlights(savedToggleValuesRef.current?.highlights ?? highlights));
        savedToggleValuesRef.current = null;
      }, 10);
    }
  };

  // Turn on highlights and diff boxes when resync is active and restore them when you leave resync
  useEffect(() => {
    updateToggleValues(selectedTool === PDFTronTools.RESYNC);
  }, [selectedTool]);

  const handleToggleClick = () => {
    selectTool(PDFTronTools.RESYNC);
  };

  // Compute disabled state and tooltip
  const isIneligibleType = useMemo(() => {
    return focusedDifference ? !RESYNC_ALLOWED_TYPES.has(focusedDifference.type) : false;
  }, [focusedDifference]);

  const isDisabled = useMemo(() => {
    return disabled || isIneligibleType;
  }, [disabled, isIneligibleType]);

  const tooltipTitle = useMemo(() => {
    if (isDisabled) {
      if (isIneligibleType) {
        return 'This difference type is not eligible for re-inspect';
      }
      return (
        <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
          Turn off multi-select to{'\n'}enable re-inspect differences
        </div>
      );
    }
    return 'Re-inspect differences';
  }, [isDisabled, isIneligibleType]);

  return (
    <>
      <Tooltip title={tooltipTitle} placement="bottom">
        <span>
          <ToggleButton
            value="resyncButton"
            data-testid="resyncButton"
            selected={selectedTool === PDFTronTools.RESYNC}
            className={classes.toggleButton}
            onClick={handleToggleClick}
            disabled={isDisabled}
            classes={{ disabled: classes.disabled }}
            disableRipple
          >
            <ResyncIcon className={classes.icon} />
          </ToggleButton>
        </span>
      </Tooltip>
    </>
  );
};

export default ResyncToggle;
